import { useState } from 'react';
import { Heart, MessageCircle, Share2, Download, Eye, X, ChevronLeft, ChevronRight, Grid, List, Filter, Image as ImageIcon } from 'lucide-react';

interface Photo {
  id: string;
  url: string;
  title: string;
  description?: string;
  uploadDate: string;
  likes: number;
  comments: number;
  isLiked: boolean;
  tags: string[];
  category: 'training' | 'competition' | 'personal' | 'achievements';
}

interface PhotoGalleryProps {
  photos: Photo[];
  isOwner?: boolean;
  onLike?: (photoId: string) => void;
  onComment?: (photoId: string) => void;
  onShare?: (photoId: string) => void;
  onDelete?: (photoId: string) => void;
}

const PhotoGallery = ({ 
  photos, 
  isOwner = false, 
  onLike, 
  onComment, 
  onShare, 
  onDelete 
}: PhotoGalleryProps) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { id: 'all', label: 'Todas', count: photos.length },
    { id: 'competition', label: 'Competencias', count: photos.filter(p => p.category === 'competition').length },
    { id: 'training', label: 'Entrenamientos', count: photos.filter(p => p.category === 'training').length },
    { id: 'achievements', label: 'Logros', count: photos.filter(p => p.category === 'achievements').length },
    { id: 'personal', label: 'Personal', count: photos.filter(p => p.category === 'personal').length }
  ];

  const filteredPhotos = selectedCategory === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === selectedCategory);

  const handleLike = (photoId: string) => {
    onLike?.(photoId);
  };

  const handleComment = (photoId: string) => {
    onComment?.(photoId);
  };

  const handleShare = (photoId: string) => {
    onShare?.(photoId);
  };

  const handleDelete = (photoId: string) => {
    if (window.confirm('¿Estás segura de que quieres eliminar esta foto?')) {
      onDelete?.(photoId);
    }
  };

  const PhotoModal = () => {
    if (!selectedPhoto) return null;

    const currentIndex = filteredPhotos.findIndex(photo => photo.id === selectedPhoto.id);
    
    const nextPhoto = () => {
      const nextIndex = (currentIndex + 1) % filteredPhotos.length;
      setSelectedPhoto(filteredPhotos[nextIndex]);
    };

    const prevPhoto = () => {
      const prevIndex = (currentIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
      setSelectedPhoto(filteredPhotos[prevIndex]);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
        <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center p-4">
          {/* Navigation */}
          {filteredPhotos.length > 1 && (
            <>
              <button 
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
              >
                <ChevronLeft size={48} />
              </button>
              <button 
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
              >
                <ChevronRight size={48} />
              </button>
            </>
          )}

          {/* Close button */}
          <button 
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X size={32} />
          </button>

          {/* Photo */}
          <div className="w-full h-full flex items-center justify-center">
            <img 
              src={selectedPhoto.url} 
              alt={selectedPhoto.title}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Photo info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
            <div className="text-white max-w-4xl mx-auto">
              <h3 className="text-xl font-semibold mb-2">{selectedPhoto.title}</h3>
              {selectedPhoto.description && (
                <p className="text-gray-300 mb-4">{selectedPhoto.description}</p>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6 text-sm">
                  <span className="flex items-center">
                    <Eye size={16} className="mr-1" />
                    {Math.floor(Math.random() * 1000) + 100} vistas
                  </span>
                  <span className="flex items-center">
                    <Heart size={16} className="mr-1" />
                    {selectedPhoto.likes} likes
                  </span>
                  <span className="flex items-center">
                    <MessageCircle size={16} className="mr-1" />
                    {selectedPhoto.comments} comentarios
                  </span>
                  <span>{new Date(selectedPhoto.uploadDate).toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => handleLike(selectedPhoto.id)}
                    className={`p-2 rounded-full transition-colors ${
                      selectedPhoto.isLiked 
                        ? 'bg-red-500 text-white' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    <Heart size={16} />
                  </button>
                  <button 
                    onClick={() => handleComment(selectedPhoto.id)}
                    className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                  >
                    <MessageCircle size={16} />
                  </button>
                  <button 
                    onClick={() => handleShare(selectedPhoto.id)}
                    className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                  >
                    <Share2 size={16} />
                  </button>
                  <button className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors">
                    <Download size={16} />
                  </button>
                </div>
              </div>

              {/* Tags */}
              {selectedPhoto.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedPhoto.tags.map((tag, index) => (
                    <span key={index} className="bg-white bg-opacity-20 text-white px-2 py-1 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Galería de Fotos</h2>
          <p className="text-gray-600 dark:text-gray-400">{photos.length} fotos</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            <Filter size={18} className="mr-2" />
            Filtros
          </button>
          
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Photo Grid/List */}
      {filteredPhotos.length > 0 ? (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
            : 'space-y-4'
        }>
          {filteredPhotos.map((photo) => (
            <div
              key={photo.id}
              className={`group relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer ${
                viewMode === 'list' ? 'flex items-center p-4' : ''
              }`}
              onClick={() => setSelectedPhoto(photo)}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={photo.url} 
                      alt={photo.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLike(photo.id);
                        }}
                        className={`p-2 rounded-full transition-colors ${
                          photo.isLiked ? 'bg-red-500 text-white' : 'bg-white bg-opacity-80 text-gray-700'
                        }`}
                      >
                        <Heart size={16} />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleComment(photo.id);
                        }}
                        className="p-2 bg-white bg-opacity-80 text-gray-700 rounded-full hover:bg-opacity-100 transition-colors"
                      >
                        <MessageCircle size={16} />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare(photo.id);
                        }}
                        className="p-2 bg-white bg-opacity-80 text-gray-700 rounded-full hover:bg-opacity-100 transition-colors"
                      >
                        <Share2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center">
                        <Heart size={12} className="mr-1" />
                        {photo.likes}
                      </div>
                      <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center">
                        <MessageCircle size={12} className="mr-1" />
                        {photo.comments}
                      </div>
                    </div>
                    
                    {isOwner && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(photo.id);
                        }}
                        className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* List View */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden mr-4 flex-shrink-0">
                    <img 
                      src={photo.url} 
                      alt={photo.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-grow">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{photo.title}</h3>
                    {photo.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-1">{photo.description}</p>
                    )}
                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Heart size={12} className="mr-1" />
                        {photo.likes} likes
                      </span>
                      <span className="flex items-center">
                        <MessageCircle size={12} className="mr-1" />
                        {photo.comments} comentarios
                      </span>
                      <span>{new Date(photo.uploadDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLike(photo.id);
                      }}
                      className={`p-2 rounded-full transition-colors ${
                        photo.isLiked 
                          ? 'text-red-500' 
                          : 'text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400'
                      }`}
                    >
                      <Heart size={16} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleComment(photo.id);
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full transition-colors"
                    >
                      <MessageCircle size={16} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShare(photo.id);
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full transition-colors"
                    >
                      <Share2 size={16} />
                    </button>
                    {isOwner && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(photo.id);
                        }}
                        className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-full transition-colors"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <ImageIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No hay fotos en esta categoría
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {isOwner ? 'Sube tu primera foto para comenzar' : 'Aún no hay fotos disponibles'}
          </p>
        </div>
      )}

      {/* Photo Modal */}
      <PhotoModal />
    </div>
  );
};

export default PhotoGallery;